import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

// Components

import AddressesInfo from './summaryIncludes/AddressesInfo';
import CitizenshipImmigrationStatusInfo from './summaryIncludes/CitizenshipImmigrationStatusInfo';
import FinancialFlowSecondSectionInfo from './summaryIncludes/FinancialFlowSecondSectionInfo';
import MilitaryInfo from './summaryIncludes/MilitaryInfo';
import RaceAndEthnicityInfo from './summaryIncludes/RaceAndEthnicityInfo';
import SsnInfo from './summaryIncludes/SsnInfo';
import SubHeader from '../../../headers/SubHeader';
import SummaryItem from './SummaryItem';
import SummaryFilter from '../summary/SummaryFilter';
import TribeInfo from './summaryIncludes/TribeInfo';

// Utils

import { getMarriageStatus } from '../../../../../utils/householdMember/getMarriageStatus';
import { normalizeNameOfPerson } from '../../../../../utils/normalize/normalizeNameOfPerson';

// Constants

import { GENDER_TYPES } from '../../../../../constants/ssap/miscellaneous/miscellaneous';
import { PAGES } from '../../../../../constants/ssap/flows/pagesTypes';
import { STATE_NAMES } from '../../../../../constants/configs/states';

// Types

import { SsapSummaryType } from '../../../../../components/ssap/SsapTypes';


const FamilyAndHouseholdSummary_NV = props => {

    const {
        addressVisibilityDueToGeneralDelivery,
        householdMembers,
        buttonContainer: ButtonContainer,
        exchangeAndProgramNames,
        hiddenSummaryItems,
        indexMapper,
        isState,
        relations,
        taxHousehold,
        taxHousehold: {
            bloodRelationship
        },
        flowType,
        serverDate,
        stateCode,
        tribes
    } = props;

    const intlApi = useIntl();

    return householdMembers.map((member, idx) => {
        const {
            applicantGuid,
            applyingForCoverageIndicator,
            citizenshipImmigrationStatus,
            citizenshipImmigrationStatus: {
                honorablyDischargedOrActiveDutyMilitaryMemberIndicator
            },
            ethnicityAndRace,
            gender,
            householdContact: {
                homeAddress,
                mailingAddress
            },
            name,
            otherAddress: {
                livingOutsideofStateTemporarilyIndicator
            },
            personId,
            socialSecurityCard
        } = member;

        const getIsMarried = (personId, bloodRelationship) => {
            return getMarriageStatus(personId, bloodRelationship);
        } ;

        const summaryId = SsapSummaryType.FAMILY_AND_HOUSEHOLD.processReplacements({ idx });

        const getGender = gender => {
            return gender && intlApi.formatMessage(
                {
                    id: gender.toLowerCase() === GENDER_TYPES.FEMALE
                        ? 'common.female'
                        : 'common.male'
                }
            );
        };

        return (
            <SummaryFilter
                key = { applicantGuid }
                indexMapper = { indexMapper }
                summaryId = { summaryId }
            >
                <div
                    className={`summary__block margin-b-20 ${idx === 0 ? '' : 'break_before'}`} >
                    <div className="summary__header">
                        <SubHeader
                            text='ssap.subheader.non_internationalized_text'
                            textReplacements={{
                                text: (
                                    <FormattedMessage
                                        id='ssap.subheader.person_name_primary_contact'
                                        values={{
                                            personName: normalizeNameOfPerson(name),
                                            primaryContact: <FormattedMessage id={idx === 0 ? 'common.primary_contact' : 'common.no_text'}/>
                                        }}
                                    />
                                )
                            }}
                            headerClassName={'gi-heading-alt'}
                            additionalElement={(
                                <ButtonContainer
                                    type="editButton_secondary"
                                    args={[idx, PAGES.PERSONAL_INFORMATION_PAGE]}
                                />
                            )}
                            additionalElementClassName={'header-btn'}
                        />
                    </div>
                    <div className="grid-row">
                        <SummaryItem
                            textCode = 'common.applying_for_coverage'
                            value = { applyingForCoverageIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].applyingForCoverageIndicator`
                                    ]
                                ]
                            }
                        />
                    </div>

                    <AddressesInfo
                        { ... {
                            applicantGuid,
                            addressVisibilityDueToGeneralDelivery,
                            homeAddress,
                            hiddenSummaryItems,
                            isState,
                            mailingAddress,
                            mailingAddressFromPortal: null,
                            pcpIndex: idx
                        } }
                    />

                    {
                        livingOutsideofStateTemporarilyIndicator !== null

                            ? (
                                <div className="grid-row">
                                    <SummaryItem
                                        replacements = { { exchangeState: STATE_NAMES[stateCode] } }
                                        textCode = 'common.are_you_living_outside_state'
                                        value = { livingOutsideofStateTemporarilyIndicator }
                                        desc = {
                                            [
                                                [
                                                    `taxHousehold.householdMember[${ idx }].otherAddress.livingOutsideofStateTemporarilyIndicator`
                                                ]
                                            ]
                                        }
                                    />
                                </div>
                            )
                            : null
                    }

                    <div className="grid-row">
                        {
                            gender &&
                                <SummaryItem
                                    textCode = 'common.gender'
                                    value = { getGender(gender) }
                                    ignoreIntl
                                    desc = {
                                        [
                                            [
                                                `taxHousehold.householdMember[${ idx }].gender`,
                                                getGender
                                            ]
                                        ]
                                    }
                                />
                        }

                        <SsnInfo
                            { ...{
                                ...socialSecurityCard,
                                idx
                            } }
                        />

                        <CitizenshipImmigrationStatusInfo
                            { ...{
                                applyingForCoverageIndicator,
                                citizenshipImmigrationStatus,
                                idx
                            } }
                        />

                        <RaceAndEthnicityInfo
                            { ...{
                                ethnicityAndRace,
                                idx
                            } }
                        />

                        <SummaryItem
                            textCode = 'label.are_you_married'
                            value = { getIsMarried(personId, bloodRelationship) }
                            desc = {
                                [
                                    [
                                        [
                                            `taxHousehold.householdMember[${ idx }].personId`,
                                            'taxHousehold.bloodRelationship'
                                        ],
                                        getIsMarried
                                    ]
                                ]
                            }
                        />

                        <MilitaryInfo
                            { ...{
                                idx,
                                honorablyDischargedOrActiveDutyMilitaryMemberIndicator
                            } }
                        />

                        <TribeInfo
                            { ...{
                                idx,
                                member,
                                tribes
                            } }
                        />

                        <FinancialFlowSecondSectionInfo
                            { ...{
                                exchangeAndProgramNames,
                                flowType,
                                idx,
                                isState,
                                member,
                                relations,
                                serverDate,
                                stateCode,
                                taxHousehold
                            } }
                        />
                    </div>
                </div>
            </SummaryFilter>
        );
    });
};

export default FamilyAndHouseholdSummary_NV;
